import React from 'react';
import { CardSectionProps } from '../../../types/compositeCards';

export const CardContent: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  const { data, context } = card;
  const { uiRole, modelType } = context;

  // Render content based on card type
  const renderContent = () => {
    if ('artifact' in data) {
      return renderArtifactContent(data.artifact);
    }
    if ('process' in data) {
      return renderProcessContent(data.process, data.allArtifacts || []);
    }
    if ('artifactItem' in data) {
      return renderArtifactItemContent(data.artifactItem, data.parentArtifact);
    }
    if ('items' in data) {
      return renderListContent(data.items, data.itemType);
    }
    return <div>Unknown content type</div>;
  };

  const renderArtifactContent = (artifact: any) => {
    const artifactInfo = getArtifactInfo(artifact);
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Basic Information */}
        {artifactInfo.length > 0 && (
          <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '500', margin: '0 0 8px 0', color: '#374151' }}>
              Information
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {artifactInfo.map((info, index) => (
                <div key={index} style={{ fontSize: '12px', color: '#6b7280' }}>
                  {info}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Items List */}
        {artifact.items && Array.isArray(artifact.items) && artifact.items.length > 0 && (
          <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '500', margin: '0 0 8px 0', color: '#374151' }}>
              Items
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {artifact.items.slice(0, 5).map((item: any, index: number) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '4px 8px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: item.state === 'active' ? '#10b981' : 
                                   item.state === 'completed' ? '#3b82f6' : '#6b7280'
                  }} />
                  <span style={{ color: '#374151' }}>{item.name}</span>
                  <span style={{ color: '#6b7280', fontSize: '10px' }}>({item.state})</span>
                </div>
              ))}
              {artifact.items.length > 5 && (
                <div style={{ fontSize: '10px', color: '#6b7280', fontStyle: 'italic' }}>
                  ... and {artifact.items.length - 5} more items
                </div>
              )}
            </div>
          </div>
        )}

        {/* Role-specific content */}
        {uiRole === 'architect' && (
          <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '500', margin: '0 0 8px 0', color: '#1e40af' }}>
              Architect View
            </h4>
            <p style={{ fontSize: '12px', color: '#1e40af', margin: 0 }}>
              This artifact can be configured and managed from the architect perspective.
            </p>
          </div>
        )}

        {uiRole === 'operator' && (
          <div style={{ padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '500', margin: '0 0 8px 0', color: '#166534' }}>
              Operator View
            </h4>
            <p style={{ fontSize: '12px', color: '#166534', margin: 0 }}>
              This artifact is ready for operational use and monitoring.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderProcessContent = (process: any, allArtifacts: any[]) => {
    const inputs: string[] = (process.attrs?.inputs && Array.isArray(process.attrs.inputs)) ? process.attrs.inputs : [];
    const outputs: string[] = (process.attrs?.outputs && Array.isArray(process.attrs.outputs)) ? process.attrs.outputs : [];
    const runs: number = (typeof process.attrs?.runs === 'number') ? process.attrs.runs : 0;

    const parseIO = (lst: string[]) =>
      lst.map((io) => {
        const [artifactType, state] = io.split(':');
        return { artifactType, state };
      });

    const inDefs = parseIO(inputs);
    const outDefs = parseIO(outputs);

    const countItemsOfTypeInState = (artifactType: string, state: string) => {
      const items = allArtifacts.filter((a) => a.type === artifactType).flatMap((a) => a.items ?? []);
      return items.filter((i) => i.state === state).length;
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Process Definition</h4>
          <p style={{ fontSize: '12px', color: '#6b7280' }}>
            {process.name} - {runs} runs completed
          </p>
        </div>
        
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Inputs</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {inDefs.map((def, index) => (
              <div key={index} style={{ fontSize: '12px', color: '#374151' }}>
                {def.artifactType} ({def.state}) - {countItemsOfTypeInState(def.artifactType, def.state)} items
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Outputs</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {outDefs.map((def, index) => (
              <div key={index} style={{ fontSize: '12px', color: '#374151' }}>
                {def.artifactType} ({def.state}) - {countItemsOfTypeInState(def.artifactType, def.state)} items
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderArtifactItemContent = (artifactItem: any, parentArtifact: any) => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '500', margin: '0 0 8px 0', color: '#374151' }}>
            Item Details
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Name: <span style={{ color: '#374151' }}>{artifactItem.name}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              State: <span style={{ color: '#374151' }}>{artifactItem.state}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Parent: <span style={{ color: '#374151' }}>{parentArtifact.name}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderListContent = (items: any[], itemType: string) => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '500', margin: '0 0 8px 0', color: '#374151' }}>
            {itemType} List ({items.length} items)
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {items.slice(0, 10).map((item, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                padding: '4px 8px',
                backgroundColor: 'white',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6'
                }} />
                <span style={{ color: '#374151' }}>{item.name || item.title || `Item ${index + 1}`}</span>
              </div>
            ))}
            {items.length > 10 && (
              <div style={{ fontSize: '10px', color: '#6b7280', fontStyle: 'italic' }}>
                ... and {items.length - 10} more items
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getArtifactInfo = (artifact: any) => {
    const info = [];
    
    // Add items count if available
    if (artifact.items && Array.isArray(artifact.items)) {
      info.push(`${artifact.items.length} items`);
    }
    
    // Add attributes if available
    if (artifact.attrs) {
      Object.entries(artifact.attrs).forEach(([key, value]) => {
        if (typeof value === 'number' || typeof value === 'string') {
          info.push(`${key}: ${value}`);
        }
      });
    }
    
    return info;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {renderContent()}
    </div>
  );
};
